import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import { IoTrashOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";
import ModalLanguageSelector from "@modalSrc/ModalLanguageSelector";
import ContentVersionHistoryButton from "@modalSrc/ContentVersionHistoryButton";
import ContentVersionHistorySelect from "@modalSrc/ContentVersionHistorySelect";
import {
  findContentVersionById,
  getCurrentContentVersionId,
  isViewingHistoricalContentVersion,
  mergeContentVersionNote,
  type ContentLanguageVersion,
} from "@/utils/contentVersionHistory";
import "@/style/sass/_contentVersionHistorySelect.scss";
import ConfirmModal from "@modals/ConfirmModal";
import HelpDetailHtmlEditorModal from "@modals/HelpDetailHtmlEditorModal";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import {
  decodeHelpLanguage,
  encodeHelpContentForStorage,
  encodeHelpDetailContentForStorage,
  encodeHelpTitleForStorage,
  normalizeHelpFormat,
  readHelpEntryFormat,
  type HelpContentFormat,
  type HelpLanguageRecord,
} from "@/utils/helpContentCodec";
import { translateTextAsync } from "@/utils/translationService";
import { getLanguageAdminLabel, getLanguageDisplayName, getTranslationLocale, filterSupportedLanguages } from "@/utils/languageUtils";

type SupportedLanguage = {
  id?: number | string;
  name?: string;
  abbrev?: string;
  ietf?: string;
};

type HelpEntry = {
  id?: number;
  section?: string;
  identifier?: string;
  companyId?: number | null;
  format?: number | string | null;
  languages?: HelpLanguageRecord[];
};

type HelpEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  helpEntry?: HelpEntry | null;
  genericEntry?: HelpEntry | null;
  companyId?: number | null;
  /** Optional; when omitted/empty the modal loads `/Translator/Languages` on open. */
  supportedLanguages?: SupportedLanguage[];
  initialLanguageId?: number | null;
  sideBySideForeignLanguageId?: number | null;
  onSaved?: () => Promise<void> | void;
};

type LanguageState = {
  id?: number;
  helpContentId?: number;
  title: string;
  content: string;
  detailContent: string;
};

function cloneLanguageStateMap(source: Record<string, LanguageState>): Record<string, LanguageState> {
  const result: Record<string, LanguageState> = {};
  Object.entries(source).forEach(([key, value]) => {
    result[key] = {
      id: value.id,
      helpContentId: value.helpContentId,
      title: value.title ?? "",
      content: value.content ?? "",
      detailContent: value.detailContent ?? "",
    };
  });
  return result;
}

function areLanguageMapsEqual(a: Record<string, LanguageState>, b: Record<string, LanguageState>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const av = a[key] ?? { title: "", content: "", detailContent: "" };
    const bv = b[key] ?? { title: "", content: "", detailContent: "" };
    if ((av.title ?? "") !== (bv.title ?? "")) return false;
    if ((av.content ?? "") !== (bv.content ?? "")) return false;
    if ((av.detailContent ?? "") !== (bv.detailContent ?? "")) return false;
  }
  return true;
}

function resolveEnglishLanguageId(
  languages: SupportedLanguage[],
  isSplitTranslationView: boolean
): string {
  if (isSplitTranslationView) return "1";
  const fromName = languages.find((lang) => /english/i.test(lang.name || ""));
  if (fromName?.id) return String(fromName.id);
  const fromAbbrev = languages.find((lang) => /^(en|eng)$/i.test((lang.abbrev || "").trim()));
  if (fromAbbrev?.id) return String(fromAbbrev.id);
  if (languages.some((lang) => Number(lang.id) === 1)) return "1";
  return "";
}

export default function HelpEditModal({
  isOpen,
  onClose,
  helpEntry,
  genericEntry,
  companyId,
  supportedLanguages: supportedLanguagesProp,
  initialLanguageId,
  sideBySideForeignLanguageId,
  onSaved,
}: HelpEditModalProps) {
  const [fetchedLanguages, setFetchedLanguages] = useState<SupportedLanguage[]>([]);
  const supportedLanguages = useMemo(() => {
    const source = (supportedLanguagesProp?.length ? supportedLanguagesProp : fetchedLanguages) ?? [];
    return filterSupportedLanguages(source) as SupportedLanguage[];
  }, [supportedLanguagesProp, fetchedLanguages]);
  const isSplitTranslationView = Number(sideBySideForeignLanguageId ?? 0) > 1;
  const foreignLanguageOptions = useMemo(
    () => supportedLanguages.filter((lang) => Number(lang.id) > 1),
    [supportedLanguages]
  );
  const [section, setSection] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>("");
  const [viewMode, setViewMode] = useState<"generic" | "company">("generic");
  const [genericLangStateById, setGenericLangStateById] = useState<Record<string, LanguageState>>({});
  const [companyLangStateById, setCompanyLangStateById] = useState<Record<string, LanguageState>>({});
  const [originalSection, setOriginalSection] = useState("");
  const [originalIdentifier, setOriginalIdentifier] = useState("");
  const [originalGenericId, setOriginalGenericId] = useState(0);
  const [originalCompanyId, setOriginalCompanyId] = useState(0);
  const [originalGenericLangStateById, setOriginalGenericLangStateById] = useState<Record<string, LanguageState>>({});
  const [originalCompanyLangStateById, setOriginalCompanyLangStateById] = useState<Record<string, LanguageState>>({});
  const [genericContentFormat, setGenericContentFormat] = useState<HelpContentFormat>(0);
  const [companyContentFormat, setCompanyContentFormat] = useState<HelpContentFormat>(0);
  const [originalGenericContentFormat, setOriginalGenericContentFormat] = useState<HelpContentFormat>(0);
  const [originalCompanyContentFormat, setOriginalCompanyContentFormat] = useState<HelpContentFormat>(0);
  const [contentLeftPanelWidth, setContentLeftPanelWidth] = useState(50);
  const contentSplitContainerRef = useRef<HTMLDivElement | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [languageSwitchConfirm, setLanguageSwitchConfirm] = useState<{
    nextLanguageId: string;
    languageLabel: string;
  } | null>(null);
  const [isDetailEditorOpen, setIsDetailEditorOpen] = useState(false);
  const [genericVersionsByLanguageId, setGenericVersionsByLanguageId] = useState<Record<number, ContentLanguageVersion[]>>({});
  const [companyVersionsByLanguageId, setCompanyVersionsByLanguageId] = useState<Record<number, ContentLanguageVersion[]>>({});
  const [selectedVersionIdByLanguageId, setSelectedVersionIdByLanguageId] = useState<Record<string, number | null>>({});

  const isCompanyMode = !!(companyId && viewMode === "company");
  const languageStateById = isCompanyMode ? companyLangStateById : genericLangStateById;
  const activeContentFormat = isCompanyMode ? companyContentFormat : genericContentFormat;

  useEffect(() => {
    if (!contentSplitContainerRef.current) return;
    contentSplitContainerRef.current.style.setProperty("--content-edit-left-width", `${contentLeftPanelWidth}%`);
  }, [contentLeftPanelWidth, activeContentFormat, selectedLanguageId, isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setLanguageSwitchConfirm(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFetchedLanguages([]);
      return;
    }
    if (supportedLanguagesProp && supportedLanguagesProp.length > 0) {
      setFetchedLanguages([]);
      return;
    }

    let cancelled = false;
    async function loadLanguagesAsync() {
      try {
        const resp = await RightTimeApi.getAsync("/Translator/Languages");
        if (cancelled || !resp?.ok || !Array.isArray(resp.jsonData)) {
          if (!cancelled) setFetchedLanguages([{ id: 1, name: "English", abbrev: "eng", ietf: "en-US" }]);
          return;
        }

        const foreign = resp.jsonData
          .map((item: Record<string, unknown>) => ({
            id: Number(item.id ?? item.Id ?? 0),
            name: String(item.name ?? item.Name ?? item.display ?? item.Display ?? ""),
            abbrev: (item.abbrev ?? item.Abbrev) as string | undefined,
            ietf: (item.ietf ?? item.Ietf ?? item.IETF) as string | undefined,
          }))
          .filter((lang: SupportedLanguage) => Number(lang.id) > 1);

        if (!cancelled) {
          setFetchedLanguages([
            { id: 1, name: "English", abbrev: "eng", ietf: "en-US" },
            ...foreign,
          ]);
        }
      } catch {
        if (!cancelled) {
          setFetchedLanguages([{ id: 1, name: "English", abbrev: "eng", ietf: "en-US" }]);
        }
      }
    }

    void loadLanguagesAsync();
    return () => {
      cancelled = true;
    };
  }, [isOpen, supportedLanguagesProp]);

  useEffect(() => {
    if (!isOpen) return;

    function mapToState(entry: HelpEntry | null | undefined, clearIds = false): Record<string, LanguageState> {
      const result: Record<string, LanguageState> = {};
      (entry?.languages ?? []).forEach((lang) => {
        const decoded = decodeHelpLanguage(lang);
        const key = String(decoded.languageId ?? "");
        if (!key || key === "0") return;
        result[key] = {
          id: clearIds ? undefined : decoded.id,
          helpContentId: clearIds ? undefined : decoded.helpContentId,
          title: decoded.title ?? "",
          content: decoded.content ?? "",
          detailContent: decoded.detailContent ?? "",
        };
      });
      return result;
    }

    // Generic state always comes from the generic (companyId=null) entry
    const genEntry = companyId ? (genericEntry ?? null) : helpEntry;
    const mappedGeneric = mapToState(genEntry);
    setGenericLangStateById(mappedGeneric);
    setOriginalGenericLangStateById(cloneLanguageStateMap(mappedGeneric));
    setOriginalGenericId(genEntry?.id ?? 0);
    setGenericContentFormat(readHelpEntryFormat(genEntry));
    setOriginalGenericContentFormat(readHelpEntryFormat(genEntry));

    // Company state: only treat helpEntry as company override when companyId actually matches.
    const isCompanySpecificEntry = !!companyId && Number(helpEntry?.companyId ?? 0) === Number(companyId);
    const mappedCompanyFromEntry = isCompanySpecificEntry ? mapToState(helpEntry) : {};
    const hasOverride = !!(isCompanySpecificEntry && helpEntry?.id && helpEntry.id > 0);
    // Do not copy generic content into company state when override does not exist.
    const mappedCompany = hasOverride ? mappedCompanyFromEntry : {};
    setCompanyLangStateById(mappedCompany);
    setOriginalCompanyLangStateById(cloneLanguageStateMap(mappedCompany));
    setOriginalCompanyId(hasOverride ? (helpEntry?.id ?? 0) : 0);
    setCompanyContentFormat(hasOverride ? readHelpEntryFormat(helpEntry) : readHelpEntryFormat(genEntry));
    setOriginalCompanyContentFormat(hasOverride ? readHelpEntryFormat(helpEntry) : readHelpEntryFormat(genEntry));

    // Default to Company only when there is meaningful company content; otherwise start on Generic.
    setViewMode(companyId && hasOverride ? "company" : "generic");

    // Section / Identifier come from the generic entry when there's a company context
    const refEntry = companyId ? (genericEntry ?? helpEntry) : helpEntry;
    const initialSection = refEntry?.section ?? "";
    const initialIdentifier = refEntry?.identifier ?? "";
    setSection(initialSection);
    setIdentifier(initialIdentifier);
    setOriginalSection(initialSection);
    setOriginalIdentifier(initialIdentifier);

    const preferredLanguageId = isSplitTranslationView
      ? Number(sideBySideForeignLanguageId)
      : Number(initialLanguageId ?? 0);
    const preferredLanguage = preferredLanguageId > 0
      ? supportedLanguages.find((lang) => Number(lang.id) === preferredLanguageId)
      : undefined;

    const resolvedEnglishLanguageId = resolveEnglishLanguageId(supportedLanguages, isSplitTranslationView);

    let firstLanguageId = "";
    if (isSplitTranslationView) {
      firstLanguageId = preferredLanguage
        ? String(preferredLanguage.id)
        : (foreignLanguageOptions[0] ? String(foreignLanguageOptions[0].id) : supportedLanguages[0] ? String(supportedLanguages[0].id) : "");
    } else if (preferredLanguage) {
      firstLanguageId = String(preferredLanguage.id);
    } else if (resolvedEnglishLanguageId) {
      firstLanguageId = resolvedEnglishLanguageId;
    } else if (foreignLanguageOptions[0]) {
      firstLanguageId = String(foreignLanguageOptions[0].id);
    } else if (supportedLanguages[0]) {
      firstLanguageId = String(supportedLanguages[0].id);
    }
    setSelectedLanguageId(firstLanguageId);
    setSelectedVersionIdByLanguageId({});
  }, [isOpen, helpEntry, genericEntry, companyId, supportedLanguages, foreignLanguageOptions, initialLanguageId, isSplitTranslationView, sideBySideForeignLanguageId]);

  useEffect(() => {
    if (!isOpen) {
      setGenericVersionsByLanguageId({});
      setCompanyVersionsByLanguageId({});
      setSelectedVersionIdByLanguageId({});
      return;
    }

    let cancelled = false;

    async function loadHelpVersionsAsync(helpId: number) {
      const resp = await RightTimeApi.getAsync(`/Help/${helpId}`);
      if (!resp?.ok || !resp.jsonData || cancelled) return null;
      const payload = resp.jsonData as HelpEntry;
      const map: Record<number, ContentLanguageVersion[]> = {};
      (payload.languages ?? []).forEach((lang) => {
        const decoded = decodeHelpLanguage(lang);
        const languageId = Number(decoded.languageId ?? 0);
        if (languageId <= 0) return;
        const rawVersions = (lang as HelpLanguageRecord & { versions?: ContentLanguageVersion[] }).versions;
        map[languageId] = Array.isArray(rawVersions) ? rawVersions : [];
      });
      return map;
    }

    void (async () => {
      const genEntry = companyId ? (genericEntry ?? null) : helpEntry;
      const genericId = Number(genEntry?.id ?? 0);
      if (genericId > 0) {
        const genericVersions = await loadHelpVersionsAsync(genericId);
        if (!cancelled && genericVersions) setGenericVersionsByLanguageId(genericVersions);
      }

      const isCompanySpecificEntry = !!companyId && Number(helpEntry?.companyId ?? 0) === Number(companyId);
      const companyHelpId = isCompanySpecificEntry ? Number(helpEntry?.id ?? 0) : 0;
      if (companyHelpId > 0) {
        const companyVersions = await loadHelpVersionsAsync(companyHelpId);
        if (!cancelled && companyVersions) setCompanyVersionsByLanguageId(companyVersions);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, helpEntry, genericEntry, companyId]);

  const currentLangState = useMemo(() => {
    return languageStateById[selectedLanguageId] ?? { title: "", content: "", detailContent: "" };
  }, [languageStateById, selectedLanguageId]);

  const activeVersionsByLanguageId = isCompanyMode ? companyVersionsByLanguageId : genericVersionsByLanguageId;
  const currentLanguageVersions = activeVersionsByLanguageId[Number(selectedLanguageId)] ?? [];
  const selectedHistoryVersionId =
    selectedVersionIdByLanguageId[selectedLanguageId] ?? getCurrentContentVersionId(currentLanguageVersions);
  const viewingHistory = isViewingHistoricalContentVersion(selectedHistoryVersionId, currentLanguageVersions);
  const selectedHistoryVersion = findContentVersionById(currentLanguageVersions, selectedHistoryVersionId);
  const displayedLangState = useMemo(() => {
    if (!viewingHistory || !selectedHistoryVersion) return currentLangState;
    return {
      ...currentLangState,
      title: selectedHistoryVersion.title ?? "",
      content: selectedHistoryVersion.content ?? "",
      detailContent: selectedHistoryVersion.detailContent ?? "",
    };
  }, [currentLangState, viewingHistory, selectedHistoryVersion]);

  useEffect(() => {
    if (!isOpen || !selectedLanguageId) return;
    setSelectedVersionIdByLanguageId((prev) => ({
      ...prev,
      [selectedLanguageId]: getCurrentContentVersionId(activeVersionsByLanguageId[Number(selectedLanguageId)] ?? []),
    }));
  }, [isOpen, viewMode, selectedLanguageId, activeVersionsByLanguageId]);

  const handleVersionNoteUpdated = (updated: ContentLanguageVersion) => {
    const languageId = Number(selectedLanguageId);
    const updater = (prev: Record<number, ContentLanguageVersion[]>) => ({
      ...prev,
      [languageId]: mergeContentVersionNote(prev[languageId] ?? [], updated),
    });
    if (isCompanyMode) {
      setCompanyVersionsByLanguageId(updater);
    } else {
      setGenericVersionsByLanguageId(updater);
    }
  };

  const englishLanguageId = useMemo(
    () => resolveEnglishLanguageId(supportedLanguages, isSplitTranslationView),
    [supportedLanguages, isSplitTranslationView]
  );

  const activeForeignLanguageId = selectedLanguageId;

  const foreignLanguage = useMemo(
    () => foreignLanguageOptions.find((lang) => String(lang.id) === activeForeignLanguageId)
      ?? supportedLanguages.find((lang) => String(lang.id) === activeForeignLanguageId)
      ?? null,
    [foreignLanguageOptions, supportedLanguages, activeForeignLanguageId]
  );

  const foreignLangState = useMemo(
    () => (activeForeignLanguageId ? languageStateById[activeForeignLanguageId] ?? { title: "", content: "", detailContent: "" } : { title: "", content: "", detailContent: "" }),
    [languageStateById, activeForeignLanguageId]
  );

  const englishState = useMemo(
    () => (englishLanguageId ? languageStateById[englishLanguageId] ?? { title: "", content: "", detailContent: "" } : { title: "", content: "", detailContent: "" }),
    [englishLanguageId, languageStateById]
  );

  const hasEnglishText = useMemo(
    () => !!(englishState.title?.trim() || englishState.content?.trim()),
    [englishState]
  );

  const translateDirection = useMemo(() => {
    if (!englishLanguageId || !activeForeignLanguageId) return null;

    const englishLanguage = isSplitTranslationView
      ? { id: 1, name: "English" }
      : supportedLanguages.find((lang) => String(lang.id) === String(englishLanguageId));
    const selectedLanguage = supportedLanguages.find((lang) => String(lang.id) === String(activeForeignLanguageId))
      ?? (isSplitTranslationView && foreignLanguage ? foreignLanguage : null);
    if (!englishLanguage || !selectedLanguage) return null;

    if (String(activeForeignLanguageId) !== String(englishLanguageId)) {
      return {
        sourceLanguageId: englishLanguageId,
        targetLanguageId: activeForeignLanguageId,
        sourceLanguage: englishLanguage,
        targetLanguage: selectedLanguage,
        sourceLabel: getLanguageDisplayName(englishLanguage),
        targetLabel: getLanguageDisplayName(selectedLanguage),
      };
    }

    if (isSplitTranslationView) return null;

    const fallbackSourceLanguage = supportedLanguages.find((lang) => {
      if (String(lang.id) === String(englishLanguageId)) return false;
      const state = languageStateById[String(lang.id)] ?? { title: "", content: "", detailContent: "" };
      return !!(state.title?.trim() || state.content?.trim());
    });
    if (!fallbackSourceLanguage) return null;

    return {
      sourceLanguageId: String(fallbackSourceLanguage.id),
      targetLanguageId: englishLanguageId,
      sourceLanguage: fallbackSourceLanguage,
      targetLanguage: englishLanguage,
      sourceLabel: getLanguageDisplayName(fallbackSourceLanguage),
      targetLabel: getLanguageDisplayName(englishLanguage),
    };
  }, [englishLanguageId, activeForeignLanguageId, supportedLanguages, languageStateById, isSplitTranslationView, foreignLanguage]);

  const hasSourceTextForTranslation = useMemo(() => {
    if (!translateDirection) return false;
    const sourceState = languageStateById[translateDirection.sourceLanguageId] ?? { title: "", content: "", detailContent: "" };
    return !!(sourceState.title?.trim() || sourceState.content?.trim());
  }, [translateDirection, languageStateById]);

  const canShowTranslateButton =
    !!translateDirection &&
    hasSourceTextForTranslation;

  const canRunTranslate = hasSourceTextForTranslation && !isSaving && !isTranslating && !viewingHistory;

  const hasConfiguredDetailHelp = useMemo(() => {
    if (viewingHistory) {
      return !!(displayedLangState.detailContent ?? "").trim();
    }
    return Object.values(languageStateById).some((state) => (state.detailContent ?? "").trim().length > 0);
  }, [languageStateById, viewingHistory, displayedLangState.detailContent]);

  const detailContentByLanguageId = useMemo(() => {
    const result: Record<string, string> = {};
    supportedLanguages.forEach((language) => {
      const key = String(language.id);
      if (viewingHistory && key === selectedLanguageId && selectedHistoryVersion) {
        result[key] = selectedHistoryVersion.detailContent ?? "";
      } else {
        result[key] = languageStateById[key]?.detailContent ?? "";
      }
    });
    return result;
  }, [languageStateById, supportedLanguages, viewingHistory, selectedLanguageId, selectedHistoryVersion]);

  function openDetailEditor() {
    setIsDetailEditorOpen(true);
  }

  function handleDetailEditorSave(updatedDetailContentByLanguageId: Record<string, string>) {
    const setter = isCompanyMode ? setCompanyLangStateById : setGenericLangStateById;
    setter((prev) => {
      const next = { ...prev };
      Object.entries(updatedDetailContentByLanguageId).forEach(([languageId, detailContent]) => {
        const existing = next[languageId] ?? { title: "", content: "", detailContent: "" };
        next[languageId] = {
          ...existing,
          detailContent,
        };
      });
      return next;
    });
  }

  function onLanguageChange(nextLanguageId: string) {
    if (!nextLanguageId) return;
    setSelectedLanguageId(nextLanguageId);
    setSelectedVersionIdByLanguageId((prev) => ({
      ...prev,
      [nextLanguageId]: getCurrentContentVersionId(activeVersionsByLanguageId[Number(nextLanguageId)] ?? []),
    }));
  }

  function onHistoryVersionChange(versionId: number | null) {
    if (!selectedLanguageId) return;
    setSelectedVersionIdByLanguageId((prev) => ({
      ...prev,
      [selectedLanguageId]: versionId,
    }));
  }

  function updateLanguageField(languageId: string, field: "title" | "content" | "detailContent", value: string) {
    if (!languageId || viewingHistory) return;
    const setter = isCompanyMode ? setCompanyLangStateById : setGenericLangStateById;
    setter((prev) => {
      const existing = prev[languageId] ?? { title: "", content: "", detailContent: "" };
      return { ...prev, [languageId]: { ...existing, [field]: value } };
    });
  }

  function updateCurrentLanguageField(field: "title" | "content" | "detailContent", value: string) {
    updateLanguageField(selectedLanguageId, field, value);
  }

  function isLanguageStateDirty(languageId: string) {
    if (!languageId) return false;
    const current = languageStateById[languageId] ?? { title: "", content: "", detailContent: "" };
    const originalMap = isCompanyMode ? originalCompanyLangStateById : originalGenericLangStateById;
    const original = originalMap[languageId] ?? { title: "", content: "", detailContent: "" };
    return (current.title ?? "") !== (original.title ?? "")
      || (current.content ?? "") !== (original.content ?? "")
      || (current.detailContent ?? "") !== (original.detailContent ?? "");
  }

  function revertLanguageToOriginal(languageId: string) {
    const originalMap = isCompanyMode ? originalCompanyLangStateById : originalGenericLangStateById;
    const original = originalMap[languageId] ?? { title: "", content: "", detailContent: "" };
    const setter = isCompanyMode ? setCompanyLangStateById : setGenericLangStateById;
    setter((prev) => ({
      ...prev,
      [languageId]: {
        id: original.id ?? prev[languageId]?.id,
        helpContentId: original.helpContentId ?? prev[languageId]?.helpContentId,
        title: original.title ?? "",
        content: original.content ?? "",
        detailContent: original.detailContent ?? "",
      },
    }));
  }

  function onSplitForeignLanguageChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextLanguageId = event.target.value;
    if (!nextLanguageId || nextLanguageId === selectedLanguageId) return;

    if (isLanguageStateDirty(selectedLanguageId)) {
      const languageLabel = getLanguageDisplayName(
        foreignLanguage ?? { id: Number(selectedLanguageId), name: selectedLanguageId }
      );
      setLanguageSwitchConfirm({ nextLanguageId, languageLabel });
      return;
    }

    setSelectedLanguageId(nextLanguageId);
  }

  async function handleLanguageSwitchSave() {
    if (!languageSwitchConfirm) return;
    const { nextLanguageId } = languageSwitchConfirm;
    const saved = await handleSaveAsync({ closeOnSuccess: false });
    if (!saved) return;
    setLanguageSwitchConfirm(null);
    setSelectedLanguageId(nextLanguageId);
  }

  function handleLanguageSwitchDiscard() {
    if (!languageSwitchConfirm) return;
    revertLanguageToOriginal(selectedLanguageId);
    setSelectedLanguageId(languageSwitchConfirm.nextLanguageId);
    setLanguageSwitchConfirm(null);
  }

  function handleLanguageSwitchCancel() {
    setLanguageSwitchConfirm(null);
  }

  function onContentSplitterMouseDown(e: ReactMouseEvent<HTMLDivElement>) {
    e.preventDefault();

    const onMouseMove = (moveEvent: MouseEvent) => {
      const container = contentSplitContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const offsetX = moveEvent.clientX - rect.left;
      const percentage = (offsetX / rect.width) * 100;
      setContentLeftPanelWidth(Math.min(75, Math.max(25, percentage)));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  function renderHelpContentField(
    languageId: string,
    value: string,
    onValueChange: (next: string) => void,
    options?: { readOnly?: boolean; labelId?: string; labelText?: string; allowResize?: boolean },
  ) {
    const readOnly = options?.readOnly ?? false;
    const labelId = options?.labelId ?? "helpBody";
    const labelText = options?.labelText ?? "Content";
    const allowResize = options?.allowResize ?? true;

    if (activeContentFormat === Enums.CONTENT_FORMAT.HTML) {
      return (
        <div className="helpEditRow helpEditHtmlContentRow">
          <label htmlFor={labelId}>{labelText}</label>
          <div
            ref={allowResize ? contentSplitContainerRef : undefined}
            className={`helpEditContentGrid contentEditGrid active${allowResize ? "" : " helpEditContentGridFixed"}`}
          >
            <div className="contentEditSection contentEditSectionRaw">
              <h4>HTML Source</h4>
              <textarea
                id={labelId}
                className="rawContent"
                rows={12}
                value={value}
                readOnly={readOnly}
                onChange={(e) => onValueChange(e.target.value)}
              />
            </div>
            <div
              className="contentEditSplitter"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize editor and preview panels"
              onMouseDown={allowResize ? onContentSplitterMouseDown : undefined}
            />
            <div className="contentEditSection contentEditSectionPreview">
              <h4>Rendered Preview</h4>
              <div className="renderedContent">{parseSanitizedHtml(value || "")}</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="helpEditRow">
        <label htmlFor={labelId}>{labelText}</label>
        <textarea
          id={labelId}
          key={`help-body-${languageId}-${activeContentFormat}`}
          rows={8}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
        />
      </div>
    );
  }

  async function handleSaveAsync(options?: { closeOnSuccess?: boolean }): Promise<boolean> {
    const closeOnSuccess = options?.closeOnSuccess !== false;
    const trimmedSection = section.trim();
    const trimmedIdentifier = identifier.trim();

    if (!trimmedIdentifier) {
      toast.error("Identifier is required.");
      return false;
    }

    function buildLanguagePayload(stateMap: Record<string, LanguageState>, format: HelpContentFormat) {
      return Object.entries(stateMap)
        .map(([languageId, value]) => {
          const normalizedLanguageId = Number(languageId);
          const normalizedTitle = (value.title ?? "").trim();
          const normalizedContent = (value.content ?? "").trim();
          const normalizedDetailContent = (value.detailContent ?? "").trim();
          return {
            id: value.id,
            helpContentId: value.helpContentId,
            languageId: normalizedLanguageId,
            title: encodeHelpTitleForStorage(normalizedTitle),
            content: encodeHelpContentForStorage(normalizedContent, format),
            detailContent: encodeHelpDetailContentForStorage(normalizedDetailContent),
            hasValidLanguageId: Number.isFinite(normalizedLanguageId) && normalizedLanguageId > 0,
            hasAnyValue: normalizedTitle.length > 0 || normalizedContent.length > 0 || normalizedDetailContent.length > 0,
          };
        })
        .filter((lang) => lang.hasValidLanguageId && lang.hasAnyValue)
        .map(({ hasAnyValue, hasValidLanguageId, ...lang }) => lang);
    }

    const metadataChanged =
      trimmedSection !== (originalSection || "") ||
      trimmedIdentifier !== (originalIdentifier || "");

    const genericChanged =
      metadataChanged ||
      genericContentFormat !== originalGenericContentFormat ||
      !areLanguageMapsEqual(genericLangStateById, originalGenericLangStateById);

    const companyChanged =
      !!companyId && (
        metadataChanged ||
        companyContentFormat !== originalCompanyContentFormat ||
        !areLanguageMapsEqual(companyLangStateById, originalCompanyLangStateById)
      );

    setIsSaving(true);
    try {
      let anySaved = false;

      if (genericChanged) {
        const genericLanguages = buildLanguagePayload(genericLangStateById, genericContentFormat);

        // Generic content cannot be blank or deleted.
        if (genericLanguages.length === 0) {
          toast.error("Generic help content cannot be blank.");
          return false;
        } else {
        const genericPayload = {
          id: originalGenericId,
          section: trimmedSection,
          identifier: trimmedIdentifier,
          companyId: null,
          format: genericContentFormat,
          languages: genericLanguages,
        };
        const genericResp = await RightTimeApi.sendAsync("/Help", genericPayload);
        if (!genericResp?.ok) {
          toast.error(genericResp?.status === 409
            ? "Help content already exists for this section and identifier."
            : "Failed to save generic help content.");
          return false;
        }
        if (genericResp.jsonData) {
          const savedFormat = readHelpEntryFormat(genericResp.jsonData);
          setGenericContentFormat(savedFormat);
          setOriginalGenericContentFormat(savedFormat);
          const savedId = Number((genericResp.jsonData as HelpEntry).id ?? 0);
          if (savedId > 0) setOriginalGenericId(savedId);
        }
        anySaved = true;
        }
      }

      if (companyChanged) {
        const companyLanguages = buildLanguagePayload(companyLangStateById, companyContentFormat);

        if (companyLanguages.length === 0) {
          // Empty company override should be removed, not persisted as an empty row.
          if (originalCompanyId > 0) {
            const deleteCompanyResp = await RightTimeApi.deleteAsync(`/Help/${originalCompanyId}`);
            if (!deleteCompanyResp?.ok) {
              toast.error("Failed to remove company help content.");
              return false;
            }
            anySaved = true;
          }
        } else {
          const companyPayload = {
            id: originalCompanyId,
            section: trimmedSection,
            identifier: trimmedIdentifier,
            companyId,
            format: companyContentFormat,
            languages: companyLanguages,
          };
          const companyResp = await RightTimeApi.sendAsync("/Help", companyPayload);
          if (!companyResp?.ok) {
            toast.error("Failed to save company help content.");
            return false;
          }
          if (companyResp.jsonData) {
            const savedFormat = readHelpEntryFormat(companyResp.jsonData);
            setCompanyContentFormat(savedFormat);
            setOriginalCompanyContentFormat(savedFormat);
            const savedId = Number((companyResp.jsonData as HelpEntry).id ?? 0);
            if (savedId > 0) setOriginalCompanyId(savedId);
          }
          anySaved = true;
        }
      }

      if (!anySaved) {
        if (closeOnSuccess) {
          toast.info("No changes to save.");
          onClose();
        }
        return true;
      }

      setOriginalGenericLangStateById(cloneLanguageStateMap(genericLangStateById));
      setOriginalCompanyLangStateById(cloneLanguageStateMap(companyLangStateById));
      setOriginalGenericContentFormat(genericContentFormat);
      setOriginalCompanyContentFormat(companyContentFormat);
      setOriginalSection(trimmedSection);
      setOriginalIdentifier(trimmedIdentifier);

      toast.success("Help content saved.");
      if (onSaved) await onSaved();
      if (closeOnSuccess) onClose();
      return true;
    } catch {
      toast.error("Failed to save help content.");
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteAsync() {
    if (!isCompanyMode) {
      toast.error("Generic help content cannot be deleted.");
      return;
    }

    // Delete company-specific override only.
    const targetEntry = helpEntry;
    if (!targetEntry?.id || targetEntry.id <= 0) return;
    if (!window.confirm("Delete this help entry?")) return;

    setIsSaving(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/Help/${targetEntry.id}`);
      if (resp?.ok) {
        toast.success("Help entry deleted.");
        if (onSaved) await onSaved();
        onClose();
      } else {
        toast.error("Failed to delete help entry.");
      }
    } catch {
      toast.error("Failed to delete help entry.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTranslateAsync() {
    if (!canShowTranslateButton || !translateDirection) return;
    if (!hasSourceTextForTranslation) {
      toast.error(`${translateDirection.sourceLabel} title or content is required before translating.`);
      return;
    }

    setIsTranslating(true);
    try {
      const activeStateById = isCompanyMode ? companyLangStateById : genericLangStateById;
      const sourceState = activeStateById[translateDirection.sourceLanguageId] ?? { title: "", content: "", detailContent: "" };

      const sourceLocale = getTranslationLocale(translateDirection.sourceLanguage, "en");
      const targetLocale = getTranslationLocale(translateDirection.targetLanguage, "en");
      const [translatedTitle, translatedContent] = await Promise.all([
        translateTextAsync(sourceState.title || "", sourceLocale, targetLocale),
        translateTextAsync(sourceState.content || "", sourceLocale, targetLocale),
      ]);

      const setter = isCompanyMode ? setCompanyLangStateById : setGenericLangStateById;
      setter((prev) => {
        const existingTarget = prev[translateDirection.targetLanguageId] ?? { title: "", content: "", detailContent: "" };
        return {
          ...prev,
          [translateDirection.targetLanguageId]: {
            ...existingTarget,
            title: translatedTitle,
            content: translatedContent,
          },
        };
      });

      setSelectedLanguageId(translateDirection.targetLanguageId);
      toast.success(
        `${isCompanyMode ? "Company" : "Generic"} ${translateDirection.targetLabel} translation added.`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to translate ${translateDirection.sourceLabel} to ${translateDirection.targetLabel}.`;
      toast.error(message);
    } finally {
      setIsTranslating(false);
    }
  }

  const isBusy = isSaving || isTranslating;

  const footer = (
    <>
      <div className="footerLeft">
        {(isCompanyMode && (helpEntry?.id ?? 0) > 0) ? (
          <button
            type="button"
            className="btn btn-danger iconButton"
            onClick={handleDeleteAsync}
            disabled={isBusy}
            title={isSaving ? "Deleting..." : "Delete help entry"}
          >
            <IoTrashOutline />
          </button>
        ) : null}

              {canShowTranslateButton ? (
                  <button
                      type="button"
                      className="btn btn-info"
                      onClick={handleTranslateAsync}
                      disabled={!canRunTranslate}
                    title={isTranslating ? "Translating..." : `Translate ${translateDirection?.sourceLabel || ""} to ${translateDirection?.targetLabel || ""}`}
                  >
                      {isTranslating ? "Translating..." : "Translate"}
                  </button>
              ) : null}

        {!isSplitTranslationView ? (
          <>
            <ContentVersionHistorySelect
              selectId="helpEditHistorySelect"
              versions={currentLanguageVersions}
              value={selectedHistoryVersionId}
              onChange={onHistoryVersionChange}
              disabled={isBusy}
              label={
                <ContentVersionHistoryButton
                  versions={currentLanguageVersions}
                  disabled={isBusy}
                  historyType="help"
                  onVersionNoteUpdated={handleVersionNoteUpdated}
                />
              }
            />
          </>
        ) : null}

      </div>
      <div className="footerCenter">
        {!isSplitTranslationView && supportedLanguages.length > 0 ? (
          <ModalLanguageSelector
            selectId="helpEditLanguageSelect"
            languages={supportedLanguages}
            value={selectedLanguageId}
            onChange={onLanguageChange}
            disabled={isBusy}
            placement="footer"
            getOptionLabel={getLanguageAdminLabel}
          />
        ) : null}
        {companyId ? (
          <span className="langToggle no-select companyToggle">
            <span
              className={`lang ${viewMode === "generic" ? "active" : ""}`}
              onClick={() => setViewMode("generic")}
              title="Edit the default help content for all companies"
            >Generic</span>
            <span
              className={`lang ${viewMode === "company" ? "active" : ""}`}
              onClick={() => setViewMode("company")}
              title="Edit help content specific to this company"
            >Company</span>
          </span>
        ) : null}
      </div>
      <div className="footerRight">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isBusy}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void handleSaveAsync()} disabled={isBusy || viewingHistory}>
          Save
        </button>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Help Content"
      className={`helpEditModal${isSplitTranslationView ? " helpEditModalSplitView" : ""}${activeContentFormat === Enums.CONTENT_FORMAT.HTML ? " helpEditModalHtmlContent" : ""}`}
      zIndex={9000}
      size={activeContentFormat === Enums.CONTENT_FORMAT.HTML || isSplitTranslationView ? "xl" : "lg"}
      footer={footer}
    >
      <div className={`helpEditForm${viewingHistory ? " viewingHistory" : ""}`}>
        {viewingHistory ? (
          <p className="contentVersionHistoryBanner">
            Viewing a previous version (read-only). Select the current version to edit or save.
          </p>
        ) : null}
        <div className="helpEditRow">
          <label htmlFor="helpSection">Section <span className="helpEditOptionalHint">(optional - leave empty for global)</span></label>
          <input
            id="helpSection"
            type="text"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            placeholder=""
            readOnly={isSplitTranslationView}
          />
        </div>

        <div className="helpEditRow">
          <label htmlFor="helpIdentifier">Identifier</label>
          <input
            id="helpIdentifier"
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder=""
            readOnly={isSplitTranslationView}
          />
        </div>

        {!isSplitTranslationView ? (
          <div className="helpEditRow">
            <label htmlFor="helpContentFormat">Content format</label>
            <select
              id="helpContentFormat"
              value={String(activeContentFormat)}
              onChange={(e) => {
                const nextFormat = normalizeHelpFormat(Number(e.target.value));
                if (isCompanyMode) {
                  setCompanyContentFormat(nextFormat);
                } else {
                  setGenericContentFormat(nextFormat);
                }
              }}
              disabled={isBusy}
            >
              <option value={String(Enums.CONTENT_FORMAT.Text)}>Text</option>
              <option value={String(Enums.CONTENT_FORMAT.HTML)}>HTML</option>
            </select>
          </div>
        ) : null}

        <div className="helpEditRow helpEditDetailRow">
          <label htmlFor="helpEditDetailedHelpButton">Detailed help</label>
          <div className="helpEditDetailActions">
            <button
              id="helpEditDetailedHelpButton"
              type="button"
              className="btn btn-secondary"
              onClick={openDetailEditor}
              disabled={isBusy}
              title={viewingHistory ? "View detailed help for this version" : "Edit detailed help"}
            >
              {viewingHistory ? "View Detailed Help" : "Edit Detailed Help"}
            </button>
            {hasConfiguredDetailHelp ? (
              <span className="helpEditDetailStatus">Detailed help configured</span>
            ) : (
              <span className="helpEditDetailStatus helpEditDetailStatusEmpty">No detailed help yet</span>
            )}
          </div>
        </div>

        {isSplitTranslationView ? (
          <div className="helpEditLanguageGrid">
            <div className="helpEditLanguageColumn">
              <h4 className="helpEditLanguageHeading">English</h4>
              <div className="helpEditRow">
                <label htmlFor="helpTitleEnglish">Title</label>
                <input
                  id="helpTitleEnglish"
                  type="text"
                  value={englishState.title}
                  readOnly
                />
              </div>
              {renderHelpContentField(
                englishLanguageId,
                englishState.content,
                () => {},
                {
                  readOnly: true,
                  labelId: "helpBodyEnglish",
                  labelText: "Content",
                  allowResize: false,
                },
              )}
            </div>
            <div className="helpEditLanguageColumn">
              <label className="helpEditLanguagePicker" htmlFor="helpForeignLanguage">
                <select
                  id="helpForeignLanguage"
                  value={selectedLanguageId}
                  onChange={onSplitForeignLanguageChange}
                  disabled={isBusy}
                  title="Select language to translate"
                >
                  {foreignLanguageOptions.map((language) => (
                    <option key={language.id} value={language.id}>
                      {getLanguageAdminLabel(language)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="helpEditRow">
                <label htmlFor="helpTitleForeign">Title</label>
                <input
                  id="helpTitleForeign"
                  type="text"
                  value={foreignLangState.title}
                  onChange={(e) => updateLanguageField(activeForeignLanguageId, "title", e.target.value)}
                />
              </div>
              {renderHelpContentField(
                activeForeignLanguageId,
                foreignLangState.content,
                (nextValue) => updateLanguageField(activeForeignLanguageId, "content", nextValue),
                {
                  labelId: "helpBodyForeign",
                  labelText: "Content",
                  allowResize: false,
                },
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="helpEditRow">
              <label htmlFor="helpTitle">Title</label>
              <input
                id="helpTitle"
                key={`help-title-${selectedLanguageId}`}
                type="text"
                value={displayedLangState.title}
                readOnly={viewingHistory}
                onChange={(e) => updateCurrentLanguageField("title", e.target.value)}
              />
            </div>

            {renderHelpContentField(
              selectedLanguageId,
              displayedLangState.content,
              (nextValue) => updateCurrentLanguageField("content", nextValue),
              { readOnly: viewingHistory },
            )}
          </>
        )}
      </div>
    </Modal>

    <HelpDetailHtmlEditorModal
      isOpen={isDetailEditorOpen}
      onClose={() => setIsDetailEditorOpen(false)}
      supportedLanguages={supportedLanguages}
      initialLanguageId={isSplitTranslationView ? activeForeignLanguageId : selectedLanguageId}
      detailContentByLanguageId={detailContentByLanguageId}
      onSave={handleDetailEditorSave}
      readOnly={viewingHistory}
    />

    <ConfirmModal
      isOpen={!!languageSwitchConfirm}
      title="Unsaved Changes"
      message={
        languageSwitchConfirm
          ? `You have unsaved changes for ${languageSwitchConfirm.languageLabel}. Save or discard before switching languages?`
          : ""
      }
      confirmText="Save"
      showCancelButton={false}
      onConfirm={() => void handleLanguageSwitchSave()}
      onCancel={handleLanguageSwitchCancel}
      extraActions={[
        {
          label: "Discard",
          className: "btn-secondary",
          onClick: handleLanguageSwitchDiscard,
        },
      ]}
      zIndex={9101}
    />
    </>
  );
}
